// ─── Academy Progress Service ─────────────────────────────────────────────────
// Sprint 098 — WorkRoamer Academy Progress Tracking
//
// localStorage-backed progress service. Pure TypeScript, no React, no side
// effects except localStorage and window event dispatch.
// All localStorage calls are wrapped in try/catch and never throw.

import { storageManager } from '@/services/storage/StorageManager';

// ─── Storage Keys ─────────────────────────────────────────────────────────────

const KEY_CHECKLIST = (slug: string): string => `nomad:academy:checklist:${slug}:v1`;
const KEY_PATHS = 'nomad:academy:paths:v1';
const KEY_ASSESSMENT = 'nomad:academy:assessment:v1';
const KEY_PRACTICE = 'nomad:academy:practice:v1';
const KEY_OPENED = 'nomad:academy:opened:v1';

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface PathProgressRecord {
  startedAt?: string;
  completedAt?: string;
}

export interface AssessmentResult {
  completedAt: string;
  answers: Record<string, string>;
  recommendedPathId: string;
  recommendedSlug: string;
  estimatedFirstEarnings: string;
  estimatedLearningHours: number;
}

export interface PracticeScore {
  score: number;
  passedAt: string;
}

// ─── Cloud Document ───────────────────────────────────────────────────────────

/** Single cloud document consolidating all 5 Academy localStorage keys. */
interface AcademyCloudDocument {
  paths: Record<string, PathProgressRecord>;
  assessment: AssessmentResult | null;
  practice: Record<string, PracticeScore[]>;
  opened: Record<string, string>;
  /** Per-slug checklist state, keyed by guide slug. */
  checklists: Record<string, Record<string, boolean>>;
}

export interface AcademyStats {
  guidesOpened: number;
  guidesCompleted: number;
  pathsStarted: number;
  pathsCompleted: number;
  practicesPassed: number;
  assessmentComplete: boolean;
  overallReadinessScore: number;
}

// ─── Internal Helpers ─────────────────────────────────────────────────────────

function readJson<T>(key: string): T | null {
  try {
    if (typeof window === 'undefined') return null;
    const raw = localStorage.getItem(key);
    if (raw === null) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeJson<T>(key: string, value: T): void {
  try {
    if (typeof window === 'undefined') return;
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Silently ignore storage errors (quota exceeded, private browsing, etc.)
  }
}

/** Collect all local Academy state into one document and push to cloud. */
function pushAcademyToCloud(): void {
  try {
    if (typeof localStorage === 'undefined') return;
    const checklists: Record<string, Record<string, boolean>> = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('nomad:academy:checklist:') && key.endsWith(':v1')) {
        const slug = key.replace('nomad:academy:checklist:', '').replace(/:v1$/, '');
        const state = readJson<Record<string, boolean>>(key);
        if (state) checklists[slug] = state;
      }
    }
    const doc: AcademyCloudDocument = {
      paths:     readJson<Record<string, PathProgressRecord>>(KEY_PATHS) ?? {},
      assessment: readJson<AssessmentResult>(KEY_ASSESSMENT),
      practice:  readJson<Record<string, PracticeScore[]>>(KEY_PRACTICE) ?? {},
      opened:    readJson<Record<string, string>>(KEY_OPENED) ?? {},
      checklists,
    };
    storageManager.push('academy', 'progress', doc);
  } catch {
    // Best-effort — never block a local write
  }
}

function dispatchProgressChanged(): void {
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('nomad:academy-progress-changed'));
    }
  } catch {
    // Silently ignore event dispatch errors
  }
  pushAcademyToCloud();
}

// ─── Checklist Functions ──────────────────────────────────────────────────────

/**
 * Returns the current checklist state for a guide.
 * Keys are item IDs; values are booleans indicating completion.
 */
export function getChecklistState(slug: string): Record<string, boolean> {
  return readJson<Record<string, boolean>>(KEY_CHECKLIST(slug)) ?? {};
}

/**
 * Sets a single checklist item for a guide and persists the change.
 */
export function setChecklistItem(slug: string, itemId: string, checked: boolean): void {
  const state = getChecklistState(slug);
  state[itemId] = checked;
  writeJson(KEY_CHECKLIST(slug), state);
  dispatchProgressChanged();
}

/**
 * Returns true if all items in the checklist are checked.
 */
export function isChecklistComplete(slug: string, totalItems: number): boolean {
  if (totalItems === 0) return false;
  const state = getChecklistState(slug);
  const checkedCount = Object.values(state).filter(Boolean).length;
  return checkedCount >= totalItems;
}

// ─── Path Progress Functions ──────────────────────────────────────────────────

/**
 * Returns the progress record for a mission path.
 * Returns an empty record if the path has not been started.
 */
export function getPathProgress(pathId: string): PathProgressRecord {
  const all = readJson<Record<string, PathProgressRecord>>(KEY_PATHS) ?? {};
  return all[pathId] ?? {};
}

/**
 * Marks a mission path as started with the current timestamp.
 * Does not overwrite an existing startedAt if already set.
 */
export function markPathStarted(pathId: string): void {
  const all = readJson<Record<string, PathProgressRecord>>(KEY_PATHS) ?? {};
  const existing = all[pathId] ?? {};
  if (!existing.startedAt) {
    all[pathId] = { ...existing, startedAt: new Date().toISOString() };
    writeJson(KEY_PATHS, all);
    dispatchProgressChanged();
  }
}

/**
 * Marks a mission path as completed with the current timestamp.
 * Also sets startedAt if it was never recorded.
 */
export function markPathCompleted(pathId: string): void {
  const all = readJson<Record<string, PathProgressRecord>>(KEY_PATHS) ?? {};
  const existing = all[pathId] ?? {};
  const now = new Date().toISOString();
  all[pathId] = {
    startedAt: existing.startedAt ?? now,
    completedAt: now,
  };
  writeJson(KEY_PATHS, all);
  dispatchProgressChanged();
}

// ─── Assessment Functions ─────────────────────────────────────────────────────

/**
 * Returns the saved assessment result, or null if not yet completed.
 */
export function getAssessmentResult(): AssessmentResult | null {
  return readJson<AssessmentResult>(KEY_ASSESSMENT);
}

/**
 * Persists the assessment result to localStorage.
 */
export function saveAssessmentResult(result: AssessmentResult): void {
  writeJson(KEY_ASSESSMENT, result);
  dispatchProgressChanged();
}

// ─── Practice Score Functions ─────────────────────────────────────────────────

/**
 * Returns all practice scores for a guide, oldest first.
 */
export function getPracticeScores(slug: string): PracticeScore[] {
  const all = readJson<Record<string, PracticeScore[]>>(KEY_PRACTICE) ?? {};
  return all[slug] ?? [];
}

/**
 * Records a new practice score for a guide.
 * A score of 80 or above is considered a pass.
 */
export function recordPracticeScore(slug: string, score: number): void {
  const all = readJson<Record<string, PracticeScore[]>>(KEY_PRACTICE) ?? {};
  const existing = all[slug] ?? [];
  existing.push({ score, passedAt: new Date().toISOString() });
  all[slug] = existing;
  writeJson(KEY_PRACTICE, all);
  dispatchProgressChanged();
}

// ─── Guide Opened Tracking ────────────────────────────────────────────────────

/**
 * Records that a guide was opened. Only records the first open time.
 */
export function markGuideOpened(slug: string): void {
  const all = readJson<Record<string, string>>(KEY_OPENED) ?? {};
  if (!all[slug]) {
    all[slug] = new Date().toISOString();
    writeJson(KEY_OPENED, all);
    dispatchProgressChanged();
  }
}

/**
 * Returns the ISO timestamp when a guide was first opened, or null.
 */
export function getGuideOpenedAt(slug: string): string | null {
  const all = readJson<Record<string, string>>(KEY_OPENED) ?? {};
  return all[slug] ?? null;
}

// ─── Academy Stats ────────────────────────────────────────────────────────────

/**
 * Computes aggregate Academy statistics across all progress data.
 *
 * overallReadinessScore formula (0–100, floored integer):
 *   30% = (guidesOpened / totalGuides) * 30
 *   20% = (guidesCompleted / totalGuides) * 20
 *   20% = (pathsStarted / totalPaths) * 20
 *   20% = assessmentComplete ? 20 : 0
 *   10% = Math.min(practicesPassed * 2, 10)
 */
export function getAcademyStats(totalGuides: number, totalPaths: number): AcademyStats {
  // Guides opened
  const openedRecord = readJson<Record<string, string>>(KEY_OPENED) ?? {};
  const guidesOpened = Object.keys(openedRecord).length;

  // Guides completed (checklists 100% complete)
  // We need to count guides where every checklist item is checked.
  // Since we don't have totalItems per guide here, we count guides that have
  // at least one item checked and all checked items are true (no false values).
  // Guides with an empty checklist are not counted as completed.
  let guidesCompleted = 0;
  try {
    if (typeof window !== 'undefined') {
      // Scan localStorage for all checklist keys belonging to this app
      const openedSlugs = Object.keys(openedRecord);
      for (const slug of openedSlugs) {
        const state = readJson<Record<string, boolean>>(KEY_CHECKLIST(slug)) ?? {};
        const entries = Object.values(state);
        if (entries.length > 0 && entries.every(Boolean)) {
          guidesCompleted++;
        }
      }
    }
  } catch {
    // Silent fallback
  }

  // Path progress
  const pathsRecord = readJson<Record<string, PathProgressRecord>>(KEY_PATHS) ?? {};
  const pathsStarted = Object.values(pathsRecord).filter(p => Boolean(p.startedAt)).length;
  const pathsCompleted = Object.values(pathsRecord).filter(p => Boolean(p.completedAt)).length;

  // Assessment
  const assessmentComplete = getAssessmentResult() !== null;

  // Practice passes (score >= 80)
  const practiceRecord = readJson<Record<string, PracticeScore[]>>(KEY_PRACTICE) ?? {};
  let practicesPassed = 0;
  for (const scores of Object.values(practiceRecord)) {
    for (const entry of scores) {
      if (entry.score >= 80) practicesPassed++;
    }
  }

  // Compute overall readiness score
  const safeGuides = totalGuides > 0 ? totalGuides : 1;
  const safePaths = totalPaths > 0 ? totalPaths : 1;

  const component1 = (guidesOpened / safeGuides) * 30;
  const component2 = (guidesCompleted / safeGuides) * 20;
  const component3 = (pathsStarted / safePaths) * 20;
  const component4 = assessmentComplete ? 20 : 0;
  const component5 = Math.min(practicesPassed * 2, 10);

  const rawScore = component1 + component2 + component3 + component4 + component5;
  const overallReadinessScore = Math.min(100, Math.floor(rawScore));

  return {
    guidesOpened,
    guidesCompleted,
    pathsStarted,
    pathsCompleted,
    practicesPassed,
    assessmentComplete,
    overallReadinessScore,
  };
}

// ─── Clear All Progress ───────────────────────────────────────────────────────

/**
 * Clears all Academy progress data from localStorage and cloud.
 * Dispatches the progress-changed event after clearing.
 */
export function clearAllAcademyProgress(): void {
  try {
    if (typeof window === 'undefined') return;

    // Clear assessment, paths, practice, and opened records
    localStorage.removeItem(KEY_PATHS);
    localStorage.removeItem(KEY_ASSESSMENT);
    localStorage.removeItem(KEY_PRACTICE);
    localStorage.removeItem(KEY_OPENED);

    // Clear all checklist keys — scan for matching pattern
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key !== null && key.startsWith('nomad:academy:checklist:') && key.endsWith(':v1')) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }
  } catch {
    // Silently ignore errors
  }

  // Remove cloud copy too.
  storageManager.remove('academy', 'progress');
  dispatchProgressChanged();
}

// ─── Cloud Hydration ──────────────────────────────────────────────────────────

/**
 * Boot hydration: adopt cloud Academy progress if strictly newer than local.
 * Called by HydrationOrchestrator. Returns true if cloud data was adopted.
 * If no cloud data exists yet, migrates current local data to cloud (first-run).
 */
export async function hydrateAcademyFromCloud(): Promise<boolean> {
  const cloud = await storageManager.hydrate<AcademyCloudDocument>('academy', 'progress');

  if (!cloud) {
    // No cloud record yet — migrate existing local data to cloud on first run.
    pushAcademyToCloud();
    return false;
  }

  try {
    if (cloud.paths)      writeJson(KEY_PATHS, cloud.paths);
    if (cloud.assessment) writeJson(KEY_ASSESSMENT, cloud.assessment);
    if (cloud.practice)   writeJson(KEY_PRACTICE, cloud.practice);
    if (cloud.opened)     writeJson(KEY_OPENED, cloud.opened);
    if (cloud.checklists) {
      for (const [slug, state] of Object.entries(cloud.checklists)) {
        writeJson(KEY_CHECKLIST(slug), state);
      }
    }
    // Notify UI — but don't call pushAcademyToCloud() here (we just received it)
    try {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('nomad:academy-progress-changed'));
      }
    } catch { /* ignore */ }
    return true;
  } catch {
    return false;
  }
}
